import React from "react";
import { Link } from "react-router-dom";
import "../../styles/moreden.variables.scss";
import "./styledButton.scss";
import { useMemo } from "react";

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;
export type ButtonSize = "x-small" | "small" | "medium" | "large" | "x-large";
export type ButtonColor = "primary" | "secondary" | "red" | "black" | "transparent";

export interface StyledButton extends ButtonProps {
  width?: string | number;
  className?: string;
  size?: ButtonSize;
  color?: ButtonColor;
  outlined?: boolean;
  disabled?: boolean;
  loading?: boolean;
  Type?: "button" | "submit" | "reset";
  to?: string | null;
  HeightUnit?: number[];
  PaddingUnit?: number[];
  FontSizeUnit?: number[];
  FontWeightUnit?: number[];
  BorderRadiusUnit?: number[];
  children?: React.ReactNode;
}

export const StyledButton = ({
  width = "auto",
  className = "",
  size = "medium",
  color = "primary",
  outlined = false,
  disabled = false,
  loading = false,
  Type = "button",
  to = null,
  HeightUnit = [24, 28, 36, 44, 52],
  PaddingUnit = [8, 8, 10, 14, 14],
  FontSizeUnit = [12, 13, 14, 16, 16],
  FontWeightUnit = [500, 500, 600, 600, 600],
  BorderRadiusUnit = [4, 4, 6, 6, 6],
  children,
  onClick,
}: StyledButton) => {
  const widthValue = typeof width === "number" ? `${width}px` : width === "auto" ? null : width;
  const throttleFunc = useMemo(() => throttling(250), []);

  const heightValue = (() => {
    switch (size) {
      case "x-large":
        return HeightUnit[4];
      case "large":
        return HeightUnit[3];
      case "small":
        return HeightUnit[1];
      case "x-small":
        return HeightUnit[0];
      case "medium":
      default:
        return HeightUnit[2];
    }
  })();

  const paddingHorizontal = (() => {
    switch (size) {
      case "x-large":
        return PaddingUnit[4];
      case "large":
        return PaddingUnit[3];
      case "small":
        return PaddingUnit[1];
      case "x-small":
        return PaddingUnit[0];
      case "medium":
      default:
        return PaddingUnit[2];
    }
  })();

  const fontSize = (() => {
    switch (size) {
      case "x-large":
        return FontSizeUnit[4];
      case "large":
        return FontSizeUnit[3];
      case "small":
        return FontSizeUnit[1];
      case "x-small":
        return FontSizeUnit[0];
      case "medium":
      default:
        return FontSizeUnit[2];
    }
  })();

  const fontWeight = (() => {
    switch (size) {
      case "x-large":
        return FontWeightUnit[4];
      case "large":
        return FontWeightUnit[3];
      case "small":
        return FontWeightUnit[1];
      case "x-small":
        return FontWeightUnit[0];
      case "medium":
      default:
        return FontWeightUnit[2];
    }
  })();

  const borderRadius = (() => {
    switch (size) {
      case "x-large":
        return BorderRadiusUnit[4];
      case "large":
        return BorderRadiusUnit[3];
      case "small":
        return BorderRadiusUnit[1];
      case "x-small":
        return BorderRadiusUnit[0];
      case "medium":
      default:
        return BorderRadiusUnit[2];
    }
  })();

  const buttonClassNames = [
    "moreden-styled-button",
    className,
    color,
    size,
    outlined ? "outlined" : "",
    disabled ? "disabled" : "",
    loading ? "loading" : "",
  ].join(" ");

  const commonButtonStyle = {
    height: `${heightValue}px`,
    padding: `0 ${paddingHorizontal}px`,
    fontSize: `${fontSize}px`,
    fontWeight: fontWeight,
    borderRadius: `${borderRadius}px`,
    pointer: "cursor",
  };

  return (
    <span className={buttonClassNames} style={widthValue ? { width: widthValue } : {}}>
      {to ? (
        <Link
          to={to}
          style={{
            lineHeight: `${heightValue - 2}px`,
            ...commonButtonStyle,
          }}
        >
          {loading ? "" : <>{children}</>}
        </Link>
      ) : onClick ? (
        <button
          type={Type}
          disabled={disabled || loading}
          style={{ ...commonButtonStyle }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            throttleFunc(() => onClick(e));
          }}
        >
          {loading ? "" : <>{children || "button"}</>}
        </button>
      ) : (
        <button
          type={Type}
          disabled={disabled || loading}
          style={{ ...commonButtonStyle }}
          onClick={() => {
            console.log("click");
          }}
        >
          {loading ? "" : <>{children || "button"}</>}
        </button>
      )}
    </span>
  );
};

export default StyledButton;

export const throttling = (throttleTimePerMs = 500) => {
  let timer: NodeJS.Timer | null = null;

  const throttleFunc = (callbackFunc: () => void) => {
    if (!timer) {
      timer = setTimeout(() => {
        timer = null;
      }, throttleTimePerMs);
      callbackFunc();
    }
  };

  return throttleFunc;
};
